/**
 * ComboboxMulti — multi-select searchable dropdown.
 *
 * Wraps DS Combobox behavior but tracks multiple values.
 * Selected items are shown as removable chips above/in the input.
 *
 * Replaces: MUI Autocomplete with `multiple` prop, styled multi-selects.
 *
 * Two variants:
 *   - `legacy` (the default) is the field exactly as the payroll FilterModal and the interview
 *     form have always used it. Nothing about it changed; none of the props marked "field
 *     variant" apply to it.
 *   - `field` is the hardened form field (STFFLG-4129): the pills live inside one bordered box
 *     that matches the DS Select beside it, the option list is portaled (so a panel with
 *     `overflow: hidden` cannot clip it), and the label, the pills and the list are wired for
 *     keyboard and assistive tech.
 *
 *   <ComboboxMulti
 *     variant="field"
 *     label="Participants"
 *     placeholder="Add a participant"
 *     options={people}
 *     value={ids}
 *     onChange={setIds}
 *     maxRows={5}
 *     showCount
 *     persistentPlaceholder
 *     hideSelectedOptions
 *     disabled={!isEditing}
 *     noValueText="No participants yet"
 *   />
 */
import React, {useState, useRef, useEffect, useLayoutEffect, useCallback} from 'react'
import {createPortal} from 'react-dom'
import styled, {css} from 'styled-components'
import {Check, X} from 'lucide-react'
import {Tooltip} from '~/src/design-system/BaseComponents'
import {EllipsisText} from '~/src/design-system/components/Table'
import {Badge, VisuallyHidden} from '~/src/design-system/Primitives'
import {useAdaptiveDropdownPosition} from '~/src/design-system/dropdownPositioning'
import {useOutsidePress} from '~/src/design-system/outsidePress'
import {Chip as RemovablePill} from './Chip'
import type {ChipTone} from './Chip'
import {FieldHelper, FieldLabel, RequiredIndicator} from './FieldParts'

export interface ComboboxMultiOption {
  value: string
  label: string
  disabled?: boolean
}

export interface ComboboxMultiOptionMeta {
  selected: boolean
  highlighted: boolean
}

/** What one pill shows in the field variant — returned by `getChipProps`. */
export interface ComboboxMultiChipProps {
  /**
   * The name on the pill and in its "Remove {name}" button. Default: the option's label, or the
   * raw value when no option carries it (e.g. a person who is no longer offered as a choice).
   */
  label?: string
  /** Pill colour. Default: the field's own pill (brand tint on navy ink). */
  tone?: ChipTone
  /** Extra content inside the pill, after the name (e.g. a "No longer in this branch" badge). */
  suffix?: React.ReactNode
  /** Test id of the pill. Default `${data-testid}-chip-${value}`. */
  'data-testid'?: string
  /** Test id of the pill's remove button. Default `${data-testid}-remove-${value}`. */
  removeTestId?: string
  /**
   * Id of an element that describes the pill's remove button, e.g. the id of the `suffix` that
   * says "No longer in this branch". The keyboard lands on that button, so a status shown only in
   * the pill would otherwise not be heard there.
   */
  removeDescribedBy?: string
}

export interface ComboboxMultiProps {
  /** Label text above the combobox */
  label?: string
  /** Hint text below the field */
  helperText?: string
  /** Error text — shows error state */
  errorText?: string
  /** Available options */
  options: ComboboxMultiOption[]
  /** Currently selected values */
  value: string[]
  /** Called when selection changes */
  onChange: (values: string[]) => void
  /** Placeholder when nothing is selected */
  placeholder?: string
  /** Required field indicator */
  required?: boolean
  /** Disabled state */
  disabled?: boolean
  /** Async search callback */
  onSearch?: (query: string) => void
  /** Loading state for async search */
  loading?: boolean
  /** Allow free-form values not in options */
  freeSolo?: boolean
  /** Custom data-testid */
  'data-testid'?: string
  /** How to display selected values: 'chips' (default) or 'count' ("N Selected") */
  displayMode?: 'chips' | 'count'
  /** Custom option renderer — replaces default label + checkmark content */
  renderOption?: (option: ComboboxMultiOption, meta: ComboboxMultiOptionMeta) => React.ReactNode
  /** Show checkbox indicator instead of checkmark for selected options */
  showCheckbox?: boolean
  /**
   * `legacy` (default) renders the field as it always has. `field` renders the hardened form
   * field: DS tokens, the pills as removable chips inside the box, a wired label and a portaled
   * option list. `displayMode` and `showCheckbox` are legacy-only.
   *
   * Every prop below applies to `variant="field"` only.
   */
  variant?: 'legacy' | 'field'
  /**
   * Field variant: how many rows of pills the box shows before the pills scroll inside the
   * border (half of the next row stays in view as the cue that there are more). The input stays
   * below them, always in view. Default: no cap, the box keeps growing.
   */
  maxRows?: number
  /**
   * Field variant: keep the placeholder once pills exist, as the add cue ("Add a participant").
   * Default false: the placeholder shows only while nothing is chosen.
   */
  persistentPlaceholder?: boolean
  /**
   * Field variant: list only the options that are not chosen yet, so a pick can only add.
   * Default false: every option is listed and picking a chosen one takes it out again.
   */
  hideSelectedOptions?: boolean
  /** Field variant: close the list after each pick. Default false: it stays open for the next. */
  closeOnSelect?: boolean
  /**
   * Field variant: view mode. The pills show with no remove controls, and there is no input and
   * no list. `disabled` renders the same way on the disabled surface.
   */
  readOnly?: boolean
  /**
   * Field variant: the text a read-only or disabled field shows when nothing is chosen
   * (e.g. "No participants yet"). Default: the placeholder.
   */
  noValueText?: string
  /** Field variant: the list text when no option is left or none matches. Default 'No matches'. */
  emptyText?: string
  /** Field variant: show the number of chosen values beside the label. */
  showCount?: boolean
  /**
   * Field variant: per-pill name, tone, extra content and test ids. `option` is undefined for a
   * value that is not among `options`.
   */
  getChipProps?: (value: string, option: ComboboxMultiOption | undefined) => ComboboxMultiChipProps
  /**
   * Field variant: the values shown as pills, when they differ from `value` — e.g. one "All" pill
   * standing for every chosen value. Each must be in `value`. Removing a pill (its button,
   * Backspace) calls `onChange` with that one value taken out of `value`. Default: `value`.
   */
  pillValues?: string[]
  /**
   * Field variant: the live-region text after an add or a removal, when the caller's own rules
   * decide what the change means (e.g. a "Select all" row). `next` is the value `onChange` gets.
   * Default: "Added Ana Ortiz. 3 selected."
   */
  describeChange?: (verb: 'Added' | 'Removed', changed: string, next: string[]) => string
  /** Field variant: accessible name for a field with no visible `label`. */
  'aria-label'?: string
  /** Field variant: id of the element that names the field, used in place of `label`. */
  'aria-labelledby'?: string
}

const Wrapper = styled.div`
  width: 100%;
`

const Label = styled.label`
  font-family: var(--font-sans);
  font-size: 14px;
  font-weight: 500;
  color: var(--neutral-800);
  display: block;
  margin-bottom: 6px;
`

const RequiredStar = styled.span`
  color: var(--error-500);
  margin-left: 2px;
`

const InputContainer = styled.div<{$hasError: boolean; $disabled: boolean; $focused: boolean}>`
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-1);
  padding: 6px var(--space-3);
  min-height: 44px;
  align-items: center;
  background: ${({$disabled}) => ($disabled ? 'var(--neutral-100)' : 'var(--white)')};
  border: 1px solid
    ${({$hasError, $focused}) =>
      $hasError ? 'var(--error-500)' : $focused ? 'var(--primary-500)' : 'var(--neutral-300)'};
  border-radius: var(--radius-sm);
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  cursor: ${({$disabled}) => ($disabled ? 'not-allowed' : 'text')};

  ${({$focused, $hasError}) =>
    $focused && !$hasError && 'box-shadow: 0 0 0 3px var(--primary-100);'}
`

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  padding: 2px var(--space-2);
  background: var(--primary-100);
  color: var(--primary-700);
  border-radius: var(--radius-sm);
  font-size: 13px;
  font-family: var(--font-sans);
  line-height: 1.4;
`

const ChipRemove = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  color: var(--primary-700);
  opacity: 0.7;

  &:hover {
    opacity: 1;
  }
`

const SearchInput = styled.input`
  flex: 1;
  min-width: 80px;
  border: none;
  outline: none;
  background: transparent;
  font-family: var(--font-sans);
  font-size: 14px;
  color: var(--neutral-900);
  padding: var(--space-1) 0;

  &::placeholder {
    color: var(--neutral-500);
  }

  &:disabled {
    cursor: not-allowed;
  }
`

const Popover = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: var(--white);
  border: 1px solid var(--neutral-200);
  border-radius: var(--radius-sm);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  max-height: 280px;
  overflow-y: auto;
  z-index: 1300;
`

const OptionItem = styled.button<{$highlighted: boolean; $selected: boolean}>`
  width: 100%;
  text-align: left;
  padding: 10px 14px;
  background: ${({$highlighted}) => ($highlighted ? 'var(--neutral-100)' : 'transparent')};
  border: none;
  font-family: var(--font-sans);
  font-size: 14px;
  color: ${({$selected}) => ($selected ? 'var(--primary-700)' : 'var(--neutral-900)')};
  font-weight: ${({$selected}) => ($selected ? '600' : '400')};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`

const EmptyState = styled.div`
  padding: var(--space-3) 14px;
  font-size: 14px;
  color: var(--neutral-600);
  text-align: center;
`

const CheckboxBox = styled.span<{$checked: boolean}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border: 2px solid ${({$checked}) => ($checked ? 'var(--primary-500)' : 'var(--neutral-400)')};
  border-radius: var(--radius-sm);
  background: ${({$checked}) => ($checked ? 'var(--primary-500)' : 'transparent')};
  margin-right: 10px;
  flex-shrink: 0;
  transition: all 0.15s ease;

  svg {
    color: white;
  }
`

const CountDisplay = styled.span`
  font-family: var(--font-sans);
  font-size: 14px;
  color: var(--neutral-600);
  padding: var(--space-1) 0;
`

const Helper = styled.span<{$error?: boolean}>`
  font-size: 12px;
  color: ${({$error}) => ($error ? 'var(--error-500)' : 'var(--neutral-600)')};
  display: block;
  margin-top: var(--space-1);
`

const RelativeWrapper = styled.div`
  position: relative;
`

/* The field as FilterModal and the interview form use it. Left exactly as it was: the hardened
   field below is a separate component so that nothing here can change by accident. */
const ComboboxMultiLegacy: React.FC<ComboboxMultiProps> = ({
  label,
  helperText,
  errorText,
  options,
  value,
  onChange,
  placeholder = 'Select…',
  required,
  disabled,
  onSearch,
  loading,
  freeSolo,
  'data-testid': testId,
  displayMode = 'chips',
  renderOption,
  showCheckbox,
}) => {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [highlighted, setHighlighted] = useState(0)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const hasError = Boolean(errorText)

  const filteredOptions = onSearch
    ? options
    : options.filter((o) => o.label.toLowerCase().includes(query.toLowerCase()))

  // Close on outside click
  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (!wrapperRef.current?.contains(e.target as Node)) {
        setOpen(false)
        setQuery('')
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const toggleOption = useCallback(
    (optValue: string) => {
      if (value.includes(optValue)) {
        onChange(value.filter((v) => v !== optValue))
      } else {
        onChange([...value, optValue])
      }
      setQuery('')
      inputRef.current?.focus()
    },
    [value, onChange],
  )

  const removeChip = useCallback(
    (optValue: string) => {
      onChange(value.filter((v) => v !== optValue))
    },
    [value, onChange],
  )

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !query && value.length > 0) {
      onChange(value.slice(0, -1))
      return
    }
    if (!open) {
      if (e.key === 'Enter' || e.key === 'ArrowDown') {
        e.preventDefault()
        setOpen(true)
      }
      return
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlighted((h) => Math.min(h + 1, filteredOptions.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlighted((h) => Math.max(h - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const opt = filteredOptions[highlighted]
      if (opt && !opt.disabled) {
        toggleOption(opt.value)
      } else if (freeSolo && query.trim()) {
        if (!value.includes(query.trim())) {
          onChange([...value, query.trim()])
        }
        setQuery('')
      }
    } else if (e.key === 'Escape') {
      e.preventDefault()
      setOpen(false)
      setQuery('')
    }
  }

  const selectedLabels = new Map(options.map((o) => [o.value, o.label]))

  return (
    <Wrapper data-testid={testId}>
      {label && (
        <Label>
          {label}
          {required && <RequiredStar aria-hidden="true"> *</RequiredStar>}
        </Label>
      )}
      <RelativeWrapper ref={wrapperRef}>
        <InputContainer
          $hasError={hasError}
          $disabled={Boolean(disabled)}
          $focused={open}
          onClick={() => {
            if (!disabled) {
              inputRef.current?.focus()
              setOpen(true)
            }
          }}>
          {displayMode === 'count' && value.length > 0 ? (
            <CountDisplay>{value.length} Selected</CountDisplay>
          ) : (
            value.map((v) => (
              <Chip key={v}>
                {selectedLabels.get(v) ?? v}
                {!disabled && (
                  <ChipRemove
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation()
                      removeChip(v)
                    }}
                    aria-label={`Remove ${selectedLabels.get(v) ?? v}`}>
                    <X size={12} />
                  </ChipRemove>
                )}
              </Chip>
            ))
          )}
          <SearchInput
            ref={inputRef}
            type="text"
            placeholder={value.length === 0 ? placeholder : ''}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value)
              setHighlighted(0)
              if (onSearch) onSearch(e.target.value)
              if (!open) setOpen(true)
            }}
            onFocus={() => setOpen(true)}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            aria-expanded={open}
            role="combobox"
            aria-autocomplete="list"
          />
        </InputContainer>
        {open && (
          <Popover role="listbox">
            {loading ? (
              <EmptyState>Loading…</EmptyState>
            ) : filteredOptions.length === 0 ? (
              <EmptyState>No matches</EmptyState>
            ) : (
              filteredOptions.map((opt, idx) => (
                <OptionItem
                  key={opt.value}
                  type="button"
                  role="option"
                  aria-selected={value.includes(opt.value)}
                  $highlighted={idx === highlighted}
                  $selected={value.includes(opt.value)}
                  disabled={opt.disabled}
                  onMouseEnter={() => setHighlighted(idx)}
                  onClick={() => toggleOption(opt.value)}>
                  {renderOption ? (
                    renderOption(opt, {
                      selected: value.includes(opt.value),
                      highlighted: idx === highlighted,
                    })
                  ) : showCheckbox ? (
                    <>
                      <CheckboxBox $checked={value.includes(opt.value)}>
                        {value.includes(opt.value) && (
                          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                            <path
                              d="M2.5 6L5 8.5L9.5 3.5"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        )}
                      </CheckboxBox>
                      <span style={{flex: 1}}>{opt.label}</span>
                    </>
                  ) : (
                    <>
                      {opt.label}
                      {value.includes(opt.value) && (
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                          <path
                            d="M3 7L6 10L11 4"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </>
                  )}
                </OptionItem>
              ))
            )}
          </Popover>
        )}
      </RelativeWrapper>
      {errorText && (
        <Helper $error role="alert">
          {errorText}
        </Helper>
      )}
      {!errorText && helperText && <Helper>{helperText}</Helper>}
    </Wrapper>
  )
}

/* =====================================================================
   variant="field" — the hardened form field (STFFLG-4129)
   One bordered box holds the pills and the input; the option list is
   portaled and positioned by the hook the DS Select and Combobox use.
   ===================================================================== */

/* 6px: the gap the DS Select leaves under its label, so the two line up side by side. */
const FieldLabelRow = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-2);
  margin-bottom: calc(var(--space-1) * 1.5);
`

/* The count badge is taller than the label's line. It is centred in a slot of no height, so a
   field with a count keeps the same label-to-box distance as one without. */
const CountSlot = styled.span`
  display: inline-flex;
  align-items: center;
  height: 0;
`

const FieldRoot = styled.div`
  width: 100%;
`

const FieldBox = styled.div<{$hasError: boolean; $disabled: boolean; $static: boolean}>`
  /* One row of pills. With the padding and the border an empty field is exactly
     --input-height-md tall, the height of the DS Select beside it. */
  --combobox-multi-row: 1.625rem;
  box-sizing: border-box;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-1) var(--space-2);
  min-height: var(--input-height-md);
  padding: var(--space-2) var(--space-3);
  background: ${({$disabled}) => ($disabled ? 'var(--neutral-100)' : 'var(--bg-card)')};
  /* --border-default, as on the DS Select and Input: the border is the control's only
     indicator, so it needs 3:1 (CLAUDE.md §3a). */
  border: 1px solid ${({$hasError}) => ($hasError ? 'var(--error-500)' : 'var(--border-default)')};
  border-radius: var(--radius-sm);
  font-family: var(--font-sans);
  transition: border-color var(--motion-fast) var(--motion-ease),
    box-shadow var(--motion-fast) var(--motion-ease);
  cursor: ${({$disabled, $static}) => ($disabled ? 'not-allowed' : $static ? 'default' : 'text')};

  ${({$static, $hasError}) =>
    !$static &&
    css`
      &:hover {
        border-color: ${$hasError ? 'var(--error-500)' : 'var(--border-strong)'};
      }

      /* The DS Select's focus treatment, copied on purpose so the two fields side by side look
         the same. It is an exception to CLAUDE.md §3a (use var(--focus-ring)): the --brand-cta
         border carries the 3:1 indicator. Move this field and the DS Select to --focus-ring
         together, never one alone. */
      &:focus-within {
        border-color: var(--brand-cta);
        box-shadow: 0 0 0 3px var(--brand-100);
      }
    `}
`

/* The pills wrap here. On one line with room to spare the input sits beside them; once they
   need a second row this block takes the full width and the input drops below it, so with
   `maxRows` only the pills scroll and the add cue never leaves the view. */
const PillArea = styled.div<{$maxRows?: number}>`
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-1);
  min-width: 0;
  max-width: 100%;

  /* The cap is the rows asked for plus half of the next one: a cut-off row is the cue that the
     pills scroll (an overlay scrollbar shows nothing until it is used). */
  ${({$maxRows}) =>
    $maxRows &&
    css`
      max-height: calc(
        var(--combobox-multi-row) * ${$maxRows + 0.5} + var(--space-1) * ${$maxRows}
      );
      overflow-y: auto;
    `}

  &:focus-visible {
    outline: none;
    border-radius: var(--radius-sm);
    box-shadow: var(--focus-ring);
  }
`

const Pill = styled(RemovablePill)`
  box-sizing: border-box;
  min-height: var(--combobox-multi-row);
  min-width: 0;
  max-width: 100%;
`

/* A pill's name: it may shrink in the pill, and the EllipsisText inside cuts a long name with "…";
   only then does its tooltip show the whole name (STFFLG-4236). */
const PillLabel = styled.span`
  min-width: 0;
  overflow: hidden;
`

const FieldInput = styled.input`
  flex: 1 1 8rem;
  min-width: 8rem;
  max-width: 100%;
  height: var(--combobox-multi-row);
  padding: 0;
  border: none;
  outline: none;
  background: transparent;
  font-family: var(--font-sans);
  font-size: var(--fs-body);
  color: var(--text-primary);

  &::placeholder {
    color: var(--text-placeholder);
    opacity: 1;
  }
`

/* In view mode this text is the field's whole content, so it keeps 4.5:1 on either surface:
   --text-secondary on the read-only (white) one, --text-primary on the disabled (grey) one,
   where --text-secondary drops to 4.45:1. */
const NoValue = styled.span<{$disabled: boolean}>`
  font-size: var(--fs-body);
  line-height: var(--combobox-multi-row);
  color: ${({$disabled}) => ($disabled ? 'var(--text-primary)' : 'var(--text-secondary)')};
`

/* The field grows with its pills, so the helper text takes its own line under the box (the
   shared FieldHelper is absolutely positioned and would sit on top of whatever follows). It
   stays right-aligned, like the helper under a DS Select. */
const InFlowHelper = styled(FieldHelper)`
  position: static;
  text-align: right;
`

/* The open list. Mirrors the DS Select's popover: fixed to the viewport, and above the modal
   layer so it is not painted behind a dialog the field sits in. */
const FieldPopover = styled.div`
  position: fixed;
  box-sizing: border-box;
  background: var(--bg-elevated);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-sm);
  box-shadow: var(--shadow-elevated);
  overflow-y: auto;
  z-index: calc(var(--z-modal) + 1);
`

const FieldOption = styled.button<{$highlighted: boolean; $selected: boolean}>`
  width: 100%;
  text-align: left;
  padding: var(--space-2) var(--space-3);
  background: ${({$highlighted}) => ($highlighted ? 'var(--bg-hover)' : 'transparent')};
  border: none;
  font-family: var(--font-sans);
  font-size: var(--fs-body);
  line-height: var(--lh-normal);
  color: ${({$selected}) => ($selected ? 'var(--primary-700)' : 'var(--text-primary)')};
  font-weight: ${({$selected}) => ($selected ? 'var(--fw-semibold)' : 'var(--fw-regular)')};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-2);

  /* The label's Tooltip wrapper: a long label truncates with an ellipsis, and only then does the
     tooltip show it whole (STFFLG-4236). */
  > span {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`

const FieldEmpty = styled.div`
  padding: var(--space-3);
  font-family: var(--font-sans);
  font-size: var(--fs-body);
  color: var(--text-secondary);
  text-align: center;
`

/* The pills' remove buttons are found by this attribute for the arrow-key navigation. */
const PILL_REMOVE_SELECTOR = '[data-combobox-multi-remove]'

let fieldCounter = 0

const ComboboxMultiField: React.FC<ComboboxMultiProps> = ({
  label,
  helperText,
  errorText,
  options,
  value,
  onChange,
  placeholder = 'Select…',
  required,
  disabled,
  onSearch,
  loading,
  freeSolo,
  'data-testid': testId,
  renderOption,
  maxRows,
  persistentPlaceholder,
  hideSelectedOptions,
  closeOnSelect,
  readOnly,
  noValueText,
  emptyText = 'No matches',
  showCount,
  getChipProps,
  pillValues,
  describeChange,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
}) => {
  const [id] = useState(() => `combobox-multi-${++fieldCounter}`)
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  // -1: no active option. The list opens with nothing active, so Enter never picks a row the
  // user did not arrow to (or type to).
  const [highlighted, setHighlighted] = useState(-1)
  const [pillsScroll, setPillsScroll] = useState(false)
  // What the polite live region last said: "Added Bob Doe. 3 selected."
  const [announcement, setAnnouncement] = useState('')
  const wrapperRef = useRef<HTMLDivElement>(null)
  const boxRef = useRef<HTMLDivElement>(null)
  const pillAreaRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  // The list is portaled to <body>, so it is outside wrapperRef — tracked separately so a press
  // inside the open list is not treated as a press outside the field.
  const popoverRef = useRef<HTMLDivElement>(null)
  const skipOpenOnFocusRef = useRef(false)
  const pendingPillFocusRef = useRef<number | null>(null)
  const revealLastPillRef = useRef(false)
  const keyboardHighlightRef = useRef(false)

  const inputId = `${id}-input`
  const labelId = `${id}-label`
  const listboxId = `${id}-listbox`
  const helperId = `${id}-helper`
  const countId = `${id}-count`
  const hintId = `${id}-hint`

  const hasError = Boolean(errorText)
  // Disabled and read-only render alike: the pills, no remove controls, no input, no list.
  const isStatic = Boolean(disabled || readOnly)
  const isOpen = open && !isStatic
  const sub = (part: string) => (testId ? `${testId}-${part}` : undefined)

  const matching = onSearch
    ? options
    : options.filter((o) => o.label.toLowerCase().includes(query.toLowerCase()))
  const listOptions = hideSelectedOptions
    ? matching.filter((o) => !value.includes(o.value))
    : matching
  // The popover is a listbox only while it holds options. "Loading…" and the empty text are a
  // plain panel, announced through the live region: a listbox with no option in it is invalid
  // ARIA, and its text is never read.
  const hasListbox = isOpen && !loading && listOptions.length > 0
  const listStatus = isOpen && !hasListbox ? (loading ? 'Loading…' : emptyText) : ''
  // The list shrinks under the highlight when a pick hides its own row. While loading there is
  // no row to point at or pick.
  const activeIndex =
    highlighted < 0 || loading ? -1 : Math.min(highlighted, listOptions.length - 1)
  const activeOption = activeIndex >= 0 ? listOptions[activeIndex] : undefined
  // Built from the value, not the row number: after a pick the row under the highlight is a
  // different person, and a changed id is what tells assistive tech the active option changed.
  const optionId = (optionValue: string) => `${listboxId}-option-${encodeURIComponent(optionValue)}`

  const optionByValue = new Map(options.map((o) => [o.value, o]))
  const nameOf = (v: string) => {
    const option = optionByValue.get(v)
    const chip = getChipProps?.(v, option) ?? {}
    return {chip, name: chip.label ?? option?.label ?? v}
  }
  const pillVals = pillValues ?? value
  const pills = pillVals.map((v) => ({value: v, ...nameOf(v)}))
  const announceChange = (verb: 'Added' | 'Removed', v: string, next: string[]) =>
    setAnnouncement(
      describeChange
        ? describeChange(verb, v, next)
        : `${verb} ${nameOf(v).name}. ${next.length} selected.`,
    )

  const close = useCallback(() => {
    setOpen(false)
    setQuery('')
    setHighlighted(-1)
  }, [])

  const position = useAdaptiveDropdownPosition({
    open: isOpen,
    anchorRef: boxRef,
    popoverRef,
    itemSelector: '[role="option"]',
    // The hook measures again whenever this number changes. A pick can wrap the pills onto a
    // new row and push the box's bottom edge down, so the number of pills is folded in with the
    // number of rows: every (rows, pills) pair gives a different number.
    itemCount: listOptions.length + pillVals.length * (options.length + 1),
    matchAnchorWidth: true,
    onAnchorHidden: close,
  })

  // Leaving edit mode with the list open must not reopen it on the way back in, nor repeat the
  // last announcement.
  useEffect(() => {
    if (!isStatic) return
    close()
    setAnnouncement('')
  }, [isStatic, close])

  // Close on a press outside the field and its portaled list. A press on a pill's or a row's
  // portaled tooltip bubble is inside, through the field's React tree (`markPressInside`).
  const markPressInside = useOutsidePress(isOpen, [wrapperRef, popoverRef], close)

  // Keep the keyboard highlight in view; a mouse hover must not scroll the list.
  useEffect(() => {
    if (!keyboardHighlightRef.current) return
    keyboardHighlightRef.current = false
    popoverRef.current
      ?.querySelector('[data-highlighted="true"]')
      ?.scrollIntoView?.({block: 'nearest'})
  }, [highlighted])

  // A view-mode field that is cut off by `maxRows` has nothing focusable inside it, so the pill
  // area itself becomes the tab stop that lets the keyboard scroll it (WCAG 2.1.1).
  useLayoutEffect(() => {
    if (!isStatic || !maxRows) {
      setPillsScroll(false)
      return
    }
    const measure = () => {
      const area = pillAreaRef.current
      setPillsScroll(Boolean(area && area.scrollHeight > area.clientHeight))
    }
    measure()
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [isStatic, maxRows, value])

  // With `maxRows` the last pills sit below the fold. After a change made from the input (a pick,
  // or Backspace on the last pill) the end of the pills is brought into view, so the user sees
  // the pill they added or what Backspace just took. A pill removed from its own button leaves
  // the scroll where it is.
  useLayoutEffect(() => {
    if (!revealLastPillRef.current) return
    revealLastPillRef.current = false
    const area = pillAreaRef.current
    if (area) area.scrollTop = area.scrollHeight
  }, [value])

  const focusInput = useCallback((openList: boolean) => {
    const input = inputRef.current
    if (!input) return
    // Focusing the input opens the list; a focus that only hands the caret back must not.
    skipOpenOnFocusRef.current = !openList && document.activeElement !== input
    input.focus()
  }, [])

  const focusPill = useCallback(
    (index: number) => {
      const buttons = pillAreaRef.current?.querySelectorAll<HTMLButtonElement>(PILL_REMOVE_SELECTOR)
      const target = buttons?.[Math.min(index, (buttons?.length ?? 0) - 1)]
      if (target) target.focus()
      else focusInput(false)
    },
    [focusInput],
  )

  // After a pill is removed from the keyboard its button is gone; focus moves to the pill that
  // took its place (or the one before, or the input when none are left) instead of being lost.
  useLayoutEffect(() => {
    const index = pendingPillFocusRef.current
    if (index === null) return
    pendingPillFocusRef.current = null
    focusPill(index)
  }, [value, focusPill])

  const removePill = (index: number) => {
    const target = pillVals[index]
    if (target === undefined) return
    const buttons = pillAreaRef.current?.querySelectorAll<HTMLButtonElement>(PILL_REMOVE_SELECTOR)
    if (buttons?.[index] && buttons[index] === document.activeElement) {
      pendingPillFocusRef.current = index
    }
    const next = value.filter((v) => v !== target)
    onChange(next)
    announceChange('Removed', target, next)
  }

  /**
   * `keepActive`: an Enter on an arrowed-to row leaves the highlight where it is, on the row
   * that takes the picked one's place, so a run of picks needs no re-arrowing. Any other pick (a
   * click, or one that clears a typed filter and so changes the whole list) leaves nothing active.
   */
  const pick = (opt: ComboboxMultiOption, keepActive = false) => {
    if (opt.disabled) return
    const removing = value.includes(opt.value)
    revealLastPillRef.current = true
    const next = removing ? value.filter((v) => v !== opt.value) : [...value, opt.value]
    onChange(next)
    announceChange(removing ? 'Removed' : 'Added', opt.value, next)
    if (!keepActive || query || closeOnSelect) setHighlighted(-1)
    setQuery('')
    if (closeOnSelect) setOpen(false)
    focusInput(!closeOnSelect)
  }

  const moveHighlight = (next: number) => {
    keyboardHighlightRef.current = true
    setHighlighted(next)
  }

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!query && pillVals.length > 0) {
      if (e.key === 'Backspace') {
        // A held key does not count: holding Backspace to clear the typed text must not carry
        // on into the pills once the text is gone.
        if (e.repeat) return
        const last = pillVals[pillVals.length - 1]!
        revealLastPillRef.current = true
        const next = value.filter((v) => v !== last)
        onChange(next)
        announceChange('Removed', last, next)
        return
      }
      // The pills are not tab stops: the left arrow walks into them from the empty input.
      if (e.key === 'ArrowLeft') {
        e.preventDefault()
        focusPill(pillVals.length - 1)
        return
      }
    }
    // Whether the arrow keys have a row to land on ("Loading…" and the empty text have none).
    const canArrow = !loading && listOptions.length > 0
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === 'ArrowDown') {
        e.preventDefault()
        setOpen(true)
        // Down opens onto the first option; Enter only opens.
        if (e.key === 'ArrowDown' && canArrow) moveHighlight(0)
      }
      return
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      if (canArrow) moveHighlight(Math.min(activeIndex + 1, listOptions.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      // From nothing active, Up lands on the last option; otherwise it stops at the first.
      if (canArrow) {
        moveHighlight(activeIndex < 0 ? listOptions.length - 1 : Math.max(activeIndex - 1, 0))
      }
    } else if (e.key === 'Enter') {
      e.preventDefault()
      // Only an option the user arrowed (or typed) to is picked. With none active, Enter does
      // nothing, so tabbing in and pressing Enter never adds the first person in the list.
      if (activeOption && !activeOption.disabled) {
        pick(activeOption, true)
      } else if (freeSolo && query.trim()) {
        const typed = query.trim()
        if (!value.includes(typed)) {
          revealLastPillRef.current = true
          const next = [...value, typed]
          onChange(next)
          announceChange('Added', typed, next)
        }
        setQuery('')
        setHighlighted(-1)
      }
    } else if (e.key === 'Escape') {
      // Only the list closes: the same Escape must not also close a Modal around the field.
      e.preventDefault()
      e.stopPropagation()
      close()
    }
  }

  const handlePillKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (e.key === 'ArrowLeft') {
      e.preventDefault()
      focusPill(Math.max(index - 1, 0))
    } else if (e.key === 'ArrowRight') {
      e.preventDefault()
      if (index >= pillVals.length - 1) focusInput(false)
      else focusPill(index + 1)
    } else if (e.key === 'Backspace' || e.key === 'Delete') {
      e.preventDefault()
      // One press, one pill: focus moves to the neighbour, so a held key would empty the field.
      if (!e.repeat) removePill(index)
    } else if (e.key === 'Enter') {
      // Enter activates the button natively. A form around the field may swallow Enter to stop
      // an accidental submit (the company Profile form does), which would also cancel that
      // activation — so the key stops here.
      e.stopPropagation()
    }
  }

  const fieldName = {
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy ?? (label && !ariaLabel ? labelId : undefined),
  }
  const describedBy =
    [
      showCount ? countId : null,
      errorText || helperText ? helperId : null,
      !isStatic && pillVals.length > 0 ? hintId : null,
    ]
      .filter(Boolean)
      .join(' ') || undefined

  const popoverStyle: React.CSSProperties = {
    top: position?.top ?? 0,
    left: position?.left ?? 0,
    width: position?.width,
    maxHeight: position?.maxHeight,
    visibility: position ? 'visible' : 'hidden',
  }

  return (
    <FieldRoot ref={wrapperRef} data-testid={testId}>
      {(label || showCount) && (
        <FieldLabelRow>
          {label && (
            <FieldLabel id={labelId} htmlFor={isStatic ? undefined : inputId}>
              {label}
              {required && <RequiredIndicator aria-hidden="true"> *</RequiredIndicator>}
            </FieldLabel>
          )}
          {showCount && (
            <CountSlot>
              <Badge variant="neutral" dot={false} aria-hidden="true" data-testid={sub('count')}>
                {value.length}
              </Badge>
              {/* What assistive tech hears, as part of the field's description only: hidden from
                  the reading order, where it would be read a second time. */}
              <VisuallyHidden id={countId} aria-hidden="true">
                {`${value.length} selected`}
              </VisuallyHidden>
            </CountSlot>
          )}
        </FieldLabelRow>
      )}
      <FieldBox
        ref={boxRef}
        $hasError={hasError}
        $disabled={Boolean(disabled)}
        $static={isStatic}
        data-testid={sub('control')}
        {...(isStatic
          ? {
              role: 'group',
              ...fieldName,
              'aria-describedby': describedBy,
              'aria-disabled': disabled || undefined,
            }
          : {
              // A press on the box must not take focus from the input (that would close the
              // list and reopen it on the click); the click then focuses the input.
              onMouseDown: (e: React.MouseEvent) => {
                markPressInside()
                if (e.target !== inputRef.current) e.preventDefault()
              },
              onClick: () => {
                focusInput(true)
                setOpen(true)
              },
            })}>
        {pills.length > 0 && (
          <PillArea
            ref={pillAreaRef}
            role="list"
            $maxRows={maxRows}
            // View mode: a scroll region with nothing focusable in it must be reachable by
            // keyboard. Edit mode: never a stop. A scroll container whose children are all out of
            // the tab order is one in Chromium unless it says otherwise, and the pill keys do
            // not work from there; the pills are reached with the left arrow instead.
            tabIndex={isStatic ? (pillsScroll ? 0 : undefined) : -1}
            data-testid={sub('chips')}
            // View mode: the group around it already carries the field's name and count, so a
            // named list would say "Participants" twice. Edit mode has no group.
            {...(isStatic ? {} : fieldName)}>
            {pills.map(({value: pillValue, chip, name}, index) => (
              <Pill
                key={pillValue}
                role="listitem"
                tone={chip.tone}
                bg={chip.tone ? undefined : 'var(--brand-100)'}
                fg={chip.tone ? undefined : 'var(--primary-800)'}
                data-testid={chip['data-testid'] ?? sub(`chip-${pillValue}`)}
                onRemove={isStatic ? undefined : () => removePill(index)}
                removeLabel={`Remove ${name}`}
                removeButtonProps={{
                  // Out of the tab order: 40 pills must not be 40 stops before the input. They
                  // are reached with the left arrow from the input, and Backspace removes.
                  tabIndex: -1,
                  'data-combobox-multi-remove': pillValue,
                  'data-testid': chip.removeTestId ?? sub(`remove-${pillValue}`),
                  'aria-describedby': chip.removeDescribedBy,
                  onKeyDown: (e) => handlePillKeyDown(e, index),
                }}>
                <PillLabel>
                  <Tooltip content={name} block>
                    <EllipsisText>{name}</EllipsisText>
                  </Tooltip>
                </PillLabel>
                {chip.suffix}
              </Pill>
            ))}
          </PillArea>
        )}
        {isStatic ? (
          pills.length === 0 && (
            <NoValue $disabled={Boolean(disabled)} data-testid={sub('empty')}>
              {noValueText ?? placeholder}
            </NoValue>
          )
        ) : (
          <FieldInput
            ref={inputRef}
            id={inputId}
            type="text"
            role="combobox"
            autoComplete="off"
            data-testid={sub('input')}
            placeholder={pillVals.length === 0 || persistentPlaceholder ? placeholder : ''}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value)
              // Typing makes the first match active, so Enter picks what the text points at.
              // Clearing the text leaves nothing active again.
              setHighlighted(e.target.value.trim() ? 0 : -1)
              if (onSearch) onSearch(e.target.value)
              if (!open) setOpen(true)
            }}
            onFocus={() => {
              if (skipOpenOnFocusRef.current) {
                skipOpenOnFocusRef.current = false
                return
              }
              setOpen(true)
            }}
            onBlur={(e) => {
              // Tabbing on (or arrowing into the pills) closes the list; it is portaled, so it
              // would otherwise stay open over whatever comes next.
              if (!popoverRef.current?.contains(e.relatedTarget as Node | null)) close()
            }}
            onKeyDown={handleInputKeyDown}
            aria-label={ariaLabel}
            aria-labelledby={ariaLabelledBy}
            aria-describedby={describedBy}
            // Expanded only while a listbox is shown: "Loading…" and the empty text are not one.
            aria-expanded={hasListbox}
            aria-controls={listboxId}
            aria-autocomplete="list"
            aria-activedescendant={
              hasListbox && activeOption ? optionId(activeOption.value) : undefined
            }
            aria-invalid={hasError || undefined}
            aria-required={required || undefined}
          />
        )}
      </FieldBox>
      {!isStatic && pillVals.length > 0 && (
        <VisuallyHidden id={hintId}>
          Press the left arrow key to move through the chosen items. Backspace or Delete removes
          one.
        </VisuallyHidden>
      )}
      {!isStatic && (
        <>
          {/* Polite live regions, in the page before anything is written to them (WCAG 4.1.3).
              One confirms each add and removal with the new count; the other says why an open
              list offers nothing ("Loading…", "No one left to add"). */}
          <VisuallyHidden role="status" aria-atomic="true" data-testid={sub('announcement')}>
            {announcement}
          </VisuallyHidden>
          <VisuallyHidden role="status" aria-atomic="true" data-testid={sub('list-status')}>
            {listStatus}
          </VisuallyHidden>
        </>
      )}
      {isOpen &&
        createPortal(
          <FieldPopover
            ref={popoverRef}
            {...(hasListbox
              ? {
                  role: 'listbox',
                  id: listboxId,
                  // With hideSelectedOptions a pick only adds, so the list is not a set of
                  // on/off choices.
                  'aria-multiselectable': hideSelectedOptions ? undefined : true,
                  ...fieldName,
                }
              : // The status panel: its text is read from the live region above.
                {'aria-hidden': true})}
            data-testid={sub('listbox')}
            style={popoverStyle}
            // Keeps focus in the input while the list (or its scrollbar, or a row's bubble) is
            // pressed, and keeps the list open.
            onMouseDown={(e) => {
              e.preventDefault()
              markPressInside()
            }}>
            {!hasListbox ? (
              <FieldEmpty>{listStatus}</FieldEmpty>
            ) : (
              listOptions.map((opt, idx) => {
                const selected = value.includes(opt.value)
                const isHighlighted = idx === activeIndex
                return (
                  <FieldOption
                    key={opt.value}
                    id={optionId(opt.value)}
                    type="button"
                    role="option"
                    tabIndex={-1}
                    aria-selected={selected}
                    data-highlighted={isHighlighted}
                    data-testid={sub(`option-${opt.value}`)}
                    $highlighted={isHighlighted}
                    $selected={selected}
                    disabled={opt.disabled}
                    onMouseEnter={() => setHighlighted(idx)}
                    onClick={() => pick(opt)}>
                    {/* The plain label is a copy of the row, so it shows only when the row is
                        cut off, adds no description to the option's name, and sits beside the
                        list (side="right"), where it covers no other row. */}
                    <Tooltip
                      content={opt.label}
                      side="right"
                      block
                      truncatedOnly
                      describeChild={false}>
                      {renderOption ? (
                        renderOption(opt, {selected, highlighted: isHighlighted})
                      ) : (
                        <span>{opt.label}</span>
                      )}
                    </Tooltip>
                    {selected && !renderOption && <Check size={14} aria-hidden />}
                  </FieldOption>
                )
              })
            )}
          </FieldPopover>,
          document.body,
        )}
      {errorText && (
        <InFlowHelper id={helperId} $error role="alert">
          {errorText}
        </InFlowHelper>
      )}
      {!errorText && helperText && <InFlowHelper id={helperId}>{helperText}</InFlowHelper>}
    </FieldRoot>
  )
}

export const ComboboxMulti: React.FC<ComboboxMultiProps> = ({variant = 'legacy', ...props}) =>
  variant === 'field' ? <ComboboxMultiField {...props} /> : <ComboboxMultiLegacy {...props} />

export default ComboboxMulti
